// Flat cross-type view of the values table, shown instead of the per-type
// AttributeValuesTable while a search query is active (see
// product-attributes-screen.tsx). Adds a "Type" column via
// AttributeValueRow's `typeLabel` prop and reuses the same row for edit/
// delete/bindings actions — but never wraps it in the sortable drag wrapper,
// which is how drag-reorder ends up unavailable while searching.

import { AttributeUsage, useDeleteAttributeValueApi } from 'apis/product-attributes';
import { useGetDealCategoryTreeApi } from 'apis/supplier-prices';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from 'design-system';
import { useState } from 'react';

import type { AttributeSearchHit } from './attribute-search';
import { AttributeValueRow } from './attribute-value-row';
import { CategoryBindingsModal } from './category-bindings-modal';
import { ValueFormModal } from './value-form-modal';

interface Props {
  hits: AttributeSearchHit[];
  usage?: AttributeUsage;
}

export const AttributeSearchResults = ({ hits, usage }: Props) => {
  const toast = useToast();
  const [editTarget, setEditTarget] = useState<AttributeSearchHit | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AttributeSearchHit | null>(null);
  const [bindingsTarget, setBindingsTarget] = useState<AttributeSearchHit | null>(null);

  const { data: treeData } = useGetDealCategoryTreeApi();
  const categoryTree = treeData?.data ?? [];

  const deleteApi = useDeleteAttributeValueApi({
    onSuccess: () => {
      toast({ title: `Attribute value "${deleteTarget?.value.label}" deleted`, status: 'success' });
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      // hooks toast server messages; this is the network-error fallback
      if (!error?.response?.data?.message) toast({ title: 'Failed to delete attribute value.', status: 'error' });
    },
  });

  return (
    <Box>
      <Text fontWeight='semibold' fontSize='md' mb='3'>
        Search results ({hits.length})
      </Text>

      <Table size='sm'>
        <Thead>
          <Tr>
            <Th>Type</Th>
            <Th>Label</Th>
            <Th>Value</Th>
            <Th>Synonyms</Th>
            <Th>Categories</Th>
            <Th>Usage</Th>
            <Th>Active</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {hits.map(({ type, value }) => (
            <AttributeValueRow
              key={value.id}
              value={value}
              typeKey={type.key}
              typeLabel={type.label}
              usage={usage}
              categoryTree={categoryTree}
              onOpenBindings={() => setBindingsTarget({ type, value })}
              onEdit={() => setEditTarget({ type, value })}
              onDelete={() => setDeleteTarget({ type, value })}
            />
          ))}
          {hits.length === 0 && (
            <Tr>
              <Td colSpan={8}>
                <Text color='gray.500' fontSize='sm' textAlign='center' py='4'>
                  No matching values.
                </Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      {editTarget && (
        <ValueFormModal
          isOpen={!!editTarget}
          onClose={() => setEditTarget(null)}
          typeId={editTarget.type.id}
          value={editTarget.value}
        />
      )}
      {bindingsTarget && (
        <CategoryBindingsModal
          isOpen={!!bindingsTarget}
          onClose={() => setBindingsTarget(null)}
          kind='value'
          targetId={bindingsTarget.value.id}
          targetLabel={bindingsTarget.value.label}
        />
      )}

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} size='md' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Attribute Value</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status='warning'>
              <AlertIcon />
              This action cannot be undone. Are you sure you want to delete “{deleteTarget?.value.label}”?
            </Alert>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={() => setDeleteTarget(null)} isDisabled={deleteApi.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={() => deleteTarget && deleteApi.mutate({ id: deleteTarget.value.id })}
              isLoading={deleteApi.isLoading}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
